import { emptySplitApi as api } from "./emptyApi"
import type {
  AssignMetaAccountRequest,
  CompleteMetaAccountUserOauthRequest,
  MetaAccess,
  MetaAccountAssignment,
  MetaAccountAssignmentsList,
  MetaAccountInventory,
  MetaAccountRemoved,
  MetaAccountUser,
  MetaAccountUserConnectResult,
  MetaAccountUserOauthUrl,
  MetaAccountUsersList,
  MoveMetaAccountAssignmentsResult,
} from "./types/meta-account.types"

/**
 * Meta access (APP-566 T2): account users (Marathon people's Facebook logins)
 * and their per-company assignments. Hand-written (not codegen), like
 * `metaBrandCredentialApi`. Every route is super admin only; no response
 * carries a token.
 *
 * Every change invalidates the four tags: the logins, the assignments, the
 * company's access card and `Partnership` (the Partnerships settings show
 * which login discovery runs on). Ids go in the path, never in a body, and
 * each body names its fields one by one.
 */
const CHANGED = [
  "MetaAccountUser",
  "MetaAccountAssignment",
  "MetaAccess",
  "Partnership",
] as const

export const metaAccountApi = api.injectEndpoints({
  endpoints: build => ({
    getMetaAccountUsers: build.query<MetaAccountUsersList, void>({
      query: () => "/meta-account-users",
      providesTags: ["MetaAccountUser"],
    }),

    /**
     * Throttled 10/min. Meta's login dialog URL; each call mints a single-use
     * state, so it is fetched through the lazy hook right before leaving and
     * never kept. With `credentialId`: Reconnect that account user (the same
     * Facebook person only).
     */
    getMetaAccountUserOauthUrl: build.query<
      MetaAccountUserOauthUrl,
      { credentialId?: string }
    >({
      query: ({ credentialId }) => ({
        url: "/meta-account-users/oauth-url",
        params: credentialId ? { credentialId } : {},
      }),
      keepUnusedDataFor: 0,
    }),

    /** Throttled 5/min. Finishes a Connect / Reconnect started from Meta access. */
    completeMetaAccountUserOauth: build.mutation<
      MetaAccountUserConnectResult,
      CompleteMetaAccountUserOauthRequest
    >({
      query: ({ code, state }) => ({
        url: "/meta-account-users/oauth",
        method: "POST",
        body: { code, state },
      }),
      invalidatesTags: [...CHANGED],
    }),

    setMetaAccountUserPaused: build.mutation<
      MetaAccountUser,
      { id: string; paused: boolean }
    >({
      query: ({ id, paused }) => ({
        url: `/meta-account-users/${id}`,
        method: "PATCH",
        body: { paused },
      }),
      invalidatesTags: [...CHANGED],
    }),

    /** 409 while the login serves a company (the message says how many). */
    removeMetaAccountUser: build.mutation<MetaAccountRemoved, { id: string }>({
      query: ({ id }) => ({
        url: `/meta-account-users/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [...CHANGED],
    }),

    setMetaAccountUserDiscoveryDefault: build.mutation<
      MetaAccountUser,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/meta-account-users/${id}/discovery-default`,
        method: "PUT",
      }),
      invalidatesTags: [...CHANGED],
    }),

    /**
     * What the login reaches, with who uses each asset. The API caches Meta's
     * answer for 10 minutes; `refresh: true` reads Meta again.
     */
    getMetaAccountUserInventory: build.query<
      MetaAccountInventory,
      { id: string; refresh?: boolean }
    >({
      query: ({ id, refresh }) => ({
        url: `/meta-account-users/${id}/inventory`,
        params: refresh ? { refresh: "true" } : {},
      }),
      providesTags: (_result, _error, { id }) => [
        { type: "MetaAccountUser", id: `inventory-${id}` },
      ],
    }),

    /** Throttled 2/min. Moves every assignment the target login reaches. */
    moveMetaAccountAssignments: build.mutation<
      MoveMetaAccountAssignmentsResult,
      { id: string; toCredentialId: string }
    >({
      query: ({ id, toCredentialId }) => ({
        url: `/meta-account-users/${id}/move-assignments`,
        method: "POST",
        body: { toCredentialId },
      }),
      invalidatesTags: [...CHANGED],
    }),

    /** Every live company, with its assignment (or null) and connector values. */
    getMetaAccountAssignments: build.query<MetaAccountAssignmentsList, void>({
      query: () => "/meta-account-assignments",
      providesTags: ["MetaAccountAssignment"],
    }),

    /** 400 / 404 / 409 / 503 carry a user-facing `message`: show it verbatim. */
    assignMetaAccount: build.mutation<
      MetaAccountAssignment,
      AssignMetaAccountRequest
    >({
      query: ({
        companyId,
        credentialId,
        adAccountId,
        igUserId,
        pageId,
        businessId,
      }) => ({
        url: `/meta-account-assignments/${companyId}`,
        method: "PUT",
        body: { credentialId, adAccountId, igUserId, pageId, businessId },
      }),
      invalidatesTags: [...CHANGED],
    }),

    setMetaAccountAssignmentPaused: build.mutation<
      MetaAccountAssignment,
      { companyId: string; paused: boolean }
    >({
      query: ({ companyId, paused }) => ({
        url: `/meta-account-assignments/${companyId}`,
        method: "PATCH",
        body: { paused },
      }),
      invalidatesTags: [...CHANGED],
    }),

    unassignMetaAccount: build.mutation<
      MetaAccountRemoved,
      { companyId: string }
    >({
      query: ({ companyId }) => ({
        url: `/meta-account-assignments/${companyId}`,
        method: "DELETE",
      }),
      invalidatesTags: [...CHANGED],
    }),

    /** The card's read model for one company; no Meta call. */
    getMetaAccess: build.query<MetaAccess, { companyId: string }>({
      query: ({ companyId }) => ({
        url: "/meta-access",
        params: { companyId },
      }),
      providesTags: ["MetaAccess"],
    }),
  }),
})

export const {
  useGetMetaAccountUsersQuery,
  useLazyGetMetaAccountUserOauthUrlQuery,
  useCompleteMetaAccountUserOauthMutation,
  useSetMetaAccountUserPausedMutation,
  useRemoveMetaAccountUserMutation,
  useSetMetaAccountUserDiscoveryDefaultMutation,
  useGetMetaAccountUserInventoryQuery,
  useLazyGetMetaAccountUserInventoryQuery,
  useMoveMetaAccountAssignmentsMutation,
  useGetMetaAccountAssignmentsQuery,
  useAssignMetaAccountMutation,
  useSetMetaAccountAssignmentPausedMutation,
  useUnassignMetaAccountMutation,
  useGetMetaAccessQuery,
} = metaAccountApi
